"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FileText, Upload, Calendar, DollarSign, Building, Phone, Mail, User, Paperclip } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
function ProposalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="rounded-full bg-green-100 p-3 text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <FileText className="size-8" />
        </div>
        <h3 className="text-xl font-bold text-foreground">{successMessage}</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          We have received your request and will review your project details shortly.
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Jane Doe" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="email" placeholder="jane@example.com" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="(555) 123-4567" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company / Organization</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Acme Corp." className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="project_details" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the services you need, goals, and any special requirements..." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel>Estimated Budget</FormLabel>
              <FormControl>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pl-9 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" {...field} value={field.value || ""}>
                    <option value="" disabled>Select a range</option>
                    <option value="Under $5,000">Under $5,000</option>
                    <option value="$5,000 – $10,000">$5,000 – $10,000</option>
                    <option value="$10,001 – $25,000">$10,001 – $25,000</option>
                    <option value="$25,001 – $50,000">$25,001 – $50,000</option>
                    <option value="Over $50,000">Over $50,000</option>
                  </select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="start_date" render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Start Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-muted-foreground" /></SafeHydrate>
                  <Input type="date" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormDescription>When would you like the project to begin?</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel>Supporting Documents</FormLabel>
            <FormControl>
              <div className="relative">
                <Paperclip className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input type="file" className="pl-9 cursor-pointer file:cursor-pointer file:text-primary file:font-semibold" onChange={e => onChange(e.target.files?.[0])} {...field} />
              </div>
            </FormControl>
            <FormDescription>Upload any relevant files (PDF, DOCX, images, etc.)</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" className="w-full rounded-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all duration-300" disabled={isSubmitting}>
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Sending Request...
          </span> : "Submit Proposal Request"}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-4">
        By submitting this form, you agree to our privacy policy and terms of service.
      </p>
    </Form>;
}
export default function RequestAProposal() {
  return <section id="request-a-proposal" className="relative w-full py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[#362242]/90 z-10 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/40 z-10" />
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1550713/img/construction_site_bg.png)] bg-cover bg-center bg-no-repeat grayscale opacity-60" />
      </div>

      <div className="container relative z-20 mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center justify-center">
          <div className="w-full max-w-3xl">
            <Card className="border-none shadow-2xl bg-white/95 backdrop-blur-sm text-slate-900">
              <CardHeader className="text-center pb-2 pt-8">
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
                  <FileText className="size-6 text-primary" />
                </div>
                <CardTitle className="text-3xl font-bold tracking-tight text-[#362242] uppercase font-sans">
                  Request a Proposal
                </CardTitle>
                <CardDescription className="text-base text-slate-600 max-w-lg mx-auto mt-2">
                  Tell us about your project needs. Our team of geotechnical experts will analyze your requirements and provide a detailed quote.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                <FormProvider formId="request_proposal_form_home" sectionName="request-proposal" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                project_details: "",
                budget: "",
                start_date: "",
                attachment: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                project_details: {
                  required: true
                }
              }} submitText="Submit Proposal Request" successMessage="Thank you! Your proposal request has been received.">
                  <ProposalForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {}
      <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-10 duration-700 delay-500">
        <Link to="/IntakeFormPage" className="no-underline">
          <Button size="lg" className="h-14 rounded-full bg-[#6B28A1] hover:bg-[#5a2187] text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 gap-3 px-6 border-2 border-white/20">Upload Plans for Quote</Button>
        </Link>
      </div>
    </section>;
}